// Customer returns — RMA (ADR-053): data access for Sales → Returns
// (PX-SAL-120) and the "Return goods" action of a shipped sales order.
//
//   sales_return_page(company, scope, term, after, limit)   keyset (created_at desc, id desc)
//   sales_return_document(id)                               header + lines + receipts + inspections
//   sales_return_candidates(order)                          what can still be returned per order line
//   sales_order_returns(order)                              the order's RMAs
//   save_sales_return / authorize / cancel / close
//   post_sales_return_receipt / reverse_sales_return_receipt

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { Page } from "@/components/DataList";
import type { StatusTone } from "@/components/ui";
import { isDemoMode, supabase } from "@/lib/supabase";

// Same lists as @patronix/domain/sales-returns (that module brings zod; the
// route config imports this file and stays in the entry chunk).
export const returnStatuses = ["draft", "authorized", "partially_received", "received", "closed", "cancelled"] as const;
export type ReturnStatus = (typeof returnStatuses)[number];
export const returnReasons = ["damaged", "defective", "wrong_item", "not_needed", "other"] as const;
export type ReturnScope = "open" | "all";
export const returnScopes: ReturnScope[] = ["open", "all"];
export const isReturnScope = (v: unknown): v is ReturnScope => v === "open" || v === "all";

export type ReturnReason = (typeof returnReasons)[number];

export const returnStatusTone = {
  draft: "neutral",
  authorized: "info",
  partially_received: "warning",
  received: "success",
  closed: "neutral",
  cancelled: "danger",
} as const satisfies Record<ReturnStatus, StatusTone>;

export interface ReturnRow {
  id: string;
  number: string | null;
  status: ReturnStatus;
  order_id: string;
  order_number: string;
  customer_id: string;
  customer_code: string;
  customer_name: string;
  customer_reference: string | null;
  line_count: number;
  created_at: string;
  authorized_at: string | null;
}

export interface ShippedLot {
  lot_id: string;
  lot_code: string;
  qty: string | number;
}

export interface ReturnLine {
  id: string;
  line_no: number;
  order_line_id: string;
  order_line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  lot_controlled: boolean;
  serial_controlled: boolean;
  warehouse_id: string;
  warehouse_code: string;
  project_code: string | null;
  base_qty: string | number;
  received_base_qty: string | number;
  base_uom_code: string;
  base_uom_decimals: number;
  reason: ReturnReason;
  inspect: boolean;
  shipped_base_qty: string | number;
  returnable_base_qty: string | number;
  shipped_lots: ShippedLot[];
}

export interface ReturnReceipt {
  id: string;
  number: string;
  delivery_note: string | null;
  note: string | null;
  reverses_receipt_id: string | null;
  reversed_by_id: string | null;
  posted_at: string;
  posted_by_name: string | null;
  line_count: number;
  lines: {
    line_no: number;
    item_code: string;
    qty: string | number;
    uom_code: string;
    location_code: string | null;
    lot_code: string | null;
    inspect: boolean;
    serial_count: number | null;
    serial_numbers: string[] | null;
  }[];
}

export interface ReturnDocument {
  id: string;
  number: string | null;
  status: ReturnStatus;
  company_id: string;
  order_id: string;
  order_number: string;
  order_date: string;
  customer_id: string;
  customer_code: string;
  customer_name: string;
  customer_reference: string | null;
  note: string | null;
  external_ref: string | null;
  revision: number;
  line_count: number;
  created_at: string;
  created_by_name: string | null;
  authorized_at: string | null;
  authorized_by_name: string | null;
  closed_at: string | null;
  cancelled_at: string | null;
  lines: ReturnLine[];
  receipts: ReturnReceipt[];
  inspections: { id: string; number: string; status: string; decision: string | null; item_code: string; qty: string | number }[];
}

export interface CandidateLine {
  order_line_id: string;
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  base_uom_code: string;
  base_uom_decimals: number;
  shipped_base_qty: string | number;
  returned_base_qty: string | number;
  returnable_base_qty: string | number;
}

export interface Candidates {
  order_id: string;
  order_number: string;
  company_id: string;
  status: string;
  customer_id: string;
  customer_code: string;
  customer_name: string;
  lines: CandidateLine[];
}

export interface ReturnLineInput {
  order_line_id: string;
  qty: string;
  reason: ReturnReason;
  inspect: boolean;
}

export interface ReceiptLineInput {
  return_line_id: string;
  qty: string;
  location_id?: string;
  lot_id?: string;
  serials?: string[];
}

export interface ListParams {
  companyId: string;
  scope: ReturnScope;
  q: string;
}

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

export const returnKeys = {
  all: ["sales", "returns"] as const,
  list: (p: ListParams) => [...returnKeys.all, "list", p] as const,
  detail: (id: string) => [...returnKeys.all, "detail", id] as const,
  candidates: (orderId: string) => [...returnKeys.all, "candidates", orderId] as const,
  ofOrder: (orderId: string) => [...returnKeys.all, "of-order", orderId] as const,
};

export async function listReturns(p: ListParams, cursor: string | null, signal: AbortSignal, size = 50): Promise<Page<ReturnRow>> {
  if (isDemoMode) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("sales_return_page", {
      p_company_id: p.companyId,
      p_scope: p.scope,
      p_term: p.q.trim() || null,
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = (data ?? []) as ReturnRow[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify({ c: last.created_at, id: last.id }) : null };
}

export function returnListQuery(p: ListParams) {
  return infiniteQueryOptions({
    queryKey: returnKeys.list(p),
    queryFn: ({ pageParam, signal }) => listReturns(p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<ReturnRow>) => last.nextCursor,
    staleTime: 30_000,
    enabled: !!p.companyId,
  });
}

export function returnDetailQuery(id: string) {
  return queryOptions({
    queryKey: returnKeys.detail(id),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc("sales_return_document", { p_id: id }).abortSignal(signal);
      if (error) throw error;
      return (data ?? null) as ReturnDocument | null;
    },
    staleTime: 30_000,
    enabled: !!id && !isDemoMode,
  });
}

export function candidatesQuery(orderId: string) {
  return queryOptions({
    queryKey: returnKeys.candidates(orderId),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc("sales_return_candidates", { p_order_id: orderId }).abortSignal(signal);
      if (error) throw error;
      return (data ?? null) as Candidates | null;
    },
    staleTime: 10_000,
    enabled: !!orderId && !isDemoMode,
  });
}

export function orderReturnsQuery(orderId: string) {
  return queryOptions({
    queryKey: returnKeys.ofOrder(orderId),
    queryFn: async ({ signal }) => {
      const { data, error } = await db().rpc("sales_order_returns", { p_order_id: orderId }).abortSignal(signal);
      if (error) throw error;
      return (data ?? []) as { id: string; number: string | null; status: ReturnStatus; created_at: string }[];
    },
    staleTime: 30_000,
    enabled: !!orderId && !isDemoMode,
  });
}

export async function saveReturn(
  id: string | null,
  companyId: string,
  orderId: string | null,
  header: { revision?: number; customer_reference: string | null; note: string | null },
  lines: ReturnLineInput[],
) {
  const { data, error } = await db().rpc("save_sales_return", {
    p_id: id,
    p_company_id: companyId,
    p_order_id: orderId,
    p_header: header,
    p_lines: lines,
  });
  if (error) throw error;
  return data as { id: string; status: ReturnStatus; revision: number };
}

export type ReturnTransition = "authorize" | "cancel" | "close";

export async function transitionReturn(id: string, action: ReturnTransition, revision: number) {
  const fn = { authorize: "authorize_sales_return", cancel: "cancel_sales_return", close: "close_sales_return" }[action];
  const { error } = await db().rpc(fn, { p_id: id, p_revision: revision });
  if (error) throw error;
}

export async function postReturnReceipt(returnId: string, deliveryNote: string | null, lines: ReceiptLineInput[]) {
  const { data, error } = await db().rpc("post_sales_return_receipt", {
    p_return_id: returnId,
    p_delivery_note: deliveryNote,
    p_lines: lines,
  });
  if (error) throw error;
  return data as { id: string; number: string; return_status: ReturnStatus; inspections: string[] };
}

export async function reverseReturnReceipt(id: string) {
  const { error } = await db().rpc("reverse_sales_return_receipt", { p_id: id });
  if (error) throw error;
}

// Known database messages → i18n; the rest is shown as it comes.
const rules: [RegExp, string][] = [
  [/changed by someone else/, "rma.err.stale"],
  [/only on shipped orders/, "rma.err.notShipped"],
  [/received on this return; close it/, "rma.err.closeInstead"],
  [/is decided; its stock has moved/, "rma.err.decided"],
  [/already reversed/, "rma.err.reversed"],
];
const lineRules: [RegExp, string][] = [
  [/^only .* can be returned/, "rma.err.over"],
  [/^lot is required/, "rma.err.lotRequired"],
  [/^lot was not shipped/, "rma.err.lotNotShipped"],
  [/^serial .* was not shipped/, "rma.err.serialNotShipped"],
  [/^the order line appears twice/, "rma.err.twice"],
  [/^more than the .* authorized/, "rma.err.overAuthorized"],
];

export function returnErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const line = /^(?:Return )?[Ll]ine (\d+):? (.+)$/.exec(message);
  if (line) {
    const rule = lineRules.find(([re]) => re.test(line[2] ?? ""));
    return tr("po.err.line", { line: line[1] ?? "", message: rule ? tr(rule[1], { detail: line[2] ?? "" }) : (line[2] ?? "") });
  }
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
